import React, { useState, useRef, RefObject, useEffect, useMemo } from 'react';
import {
    Button, Grid, CircularProgress,
    Dialog, DialogContent, DialogTitle, DialogActions,
    Paper, IconButton, Box, Typography

} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PaperProps } from "@mui/material/Paper";
import Draggable from 'react-draggable';
import CloseIcon from '@mui/icons-material/Close';
import { TimekeeperService } from 'services/TimekeeperService';
import { DatePattern } from 'repository/timekeeper/@types/DatePattern';
import { AccrualDate } from 'repository/timekeeper/@types/AccrualDate';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

interface InfoProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    selectedRecord: any;
    cleanSelected: () => void;
}

enum FrequencyUnitTime {
    DIAS = "DIAS",
    SEMANAS = "SEMANAS",
    MESES = "MESES",
    ANOS = "ANOS",
    PERIODO_PAGO = "PERIODO_PAGO"
}

enum PatternStartDateEnum {
    FECHA_REFERENCIA = "FECHA_REFERENCIA",
    CERCANO_FECHA_REFERENCIA = "CERCANO_FECHA_REFERENCIA",
    ANTES_FECHA_REFERENCIA = "ANTES_FECHA_REFERENCIA",
    DESPUES_FECHA_REFERENCIA = "DESPUES_FECHA_REFERENCIA"
}

enum CompensationTypeEnum {
    ANTES = "ANTES",
    DESPUES = "DESPUES",
    NONE = "none",
    DISABLED = "disabled"
}

function PaperComponent(props: PaperProps) {
    const nodeRef = useRef<HTMLDivElement>(null);
    return (
        <Draggable
            nodeRef={nodeRef as RefObject<HTMLDivElement>}
            handle="#draggable-dialog-title"
            cancel={'[class*="MuiDialogContent-root"]'}
        >
            <Paper {...props} ref={nodeRef} />
        </Draggable>
    );
}

const timekeeperService = new TimekeeperService();

const formatDurationToHHMM = (raw?: string | null) => {
    if (!raw) {
        return '';
    }

    // ISO 8601 duration: PT#H#M#S
    if (/^PT/i.test(raw)) {
        const parsed = dayjs.duration(raw);
        const totalMinutes = Math.floor(parsed.asMinutes());
        if (Number.isFinite(totalMinutes)) {
            const outHours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
            const outMinutes = String(totalMinutes % 60).padStart(2, '0');
            return `${outHours}:${outMinutes}`;
        }
    }

    // HH:MM:SS or HH:MM
    const parts = raw.split(':').map(Number);
    if ((parts.length === 2 || parts.length === 3) && parts.every((part) => !Number.isNaN(part))) {
        const parsed = dayjs.duration({
            hours: parts[0] ?? 0,
            minutes: parts[1] ?? 0,
            seconds: parts[2] ?? 0,
        });
        const totalMinutes = Math.floor(parsed.asMinutes());
        const outHours = String(Math.floor(totalMinutes / 60)).padStart(2, '0');
        const outMinutes = String(totalMinutes % 60).padStart(2, '0');
        return `${outHours}:${outMinutes}`;
    }

    return raw;
};

const InfoDatePattern: React.FC<InfoProps> = ({ open, setOpen, selectedRecord, cleanSelected }) => {
    const { t } = useTranslation()

    // Mapa de traducciones de frecuencias
    const frequencyOptions = useMemo(() => [
        { value: FrequencyUnitTime.DIAS, label: t('accruals.date_patterns.dialog.inputs.options_frequency.days') },
        { value: FrequencyUnitTime.SEMANAS, label: t('accruals.date_patterns.dialog.inputs.options_frequency.weeks') },
        { value: FrequencyUnitTime.MESES, label: t('accruals.date_patterns.dialog.inputs.options_frequency.months') },
        { value: FrequencyUnitTime.ANOS, label: t('accruals.date_patterns.dialog.inputs.options_frequency.years') },
        { value: FrequencyUnitTime.PERIODO_PAGO, label: t('accruals.date_patterns.dialog.inputs.options_frequency.periods_of_pay') }
    ], [t]);

    const patternStartDateOptions = useMemo(() => [
        { value: PatternStartDateEnum.FECHA_REFERENCIA, label: t('accruals.date_patterns.dialog.inputs.options_start_of_pattern.start_of_the_reference_date') },
        { value: PatternStartDateEnum.CERCANO_FECHA_REFERENCIA, label: t('accruals.date_patterns.dialog.inputs.options_start_of_pattern.start_as_close_as_possible_to_the_reference_date') },
        { value: PatternStartDateEnum.ANTES_FECHA_REFERENCIA, label: t('accruals.date_patterns.dialog.inputs.options_start_of_pattern.start_on_or_before_the_reference_date') },
        { value: PatternStartDateEnum.DESPUES_FECHA_REFERENCIA, label: t('accruals.date_patterns.dialog.inputs.options_start_of_pattern.start_on_or_after_the_reference_date') }
    ], [t]);

    const compensationTypeOptions = useMemo(() => [
        { value: CompensationTypeEnum.ANTES, label: t('accruals.date_patterns.dialog.inputs.options_chronological_difference.before') },
        { value: CompensationTypeEnum.DESPUES, label: t('accruals.date_patterns.dialog.inputs.options_chronological_difference.after') }
    ], [t]);

    const daysOfWeekOptions = useMemo(() => [
        { value: 1, label: t('general.days_week.monday') },
        { value: 2, label: t('general.days_week.tuesday') },
        { value: 3, label: t('general.days_week.wednesday') },
        { value: 4, label: t('general.days_week.thursday') },
        { value: 5, label: t('general.days_week.friday') },
        { value: 6, label: t('general.days_week.saturday') },
        { value: 7, label: t('general.days_week.sunday') }
    ], [t]);

    const MonthOfYear = useMemo(() => [
        { value: 1, label: t('general.months.january') },
        { value: 2, label: t('general.months.february') },
        { value: 3, label: t('general.months.march') },
        { value: 4, label: t('general.months.april') },
        { value: 5, label: t('general.months.may') },
        { value: 6, label: t('general.months.june') },
        { value: 7, label: t('general.months.july') },
        { value: 8, label: t('general.months.august') },
        { value: 9, label: t('general.months.september') },
        { value: 10, label: t('general.months.october') },
        { value: 11, label: t('general.months.november') },
        { value: 12, label: t('general.months.december') }
    ], [t]);

    const [accrual_date, setAccrualDate] = useState<AccrualDate[]>([]);
    const [date_pattern, setDatePattern] = useState<DatePattern | null>(null);
    const [loading, setLoading] = useState<boolean>(false);



    useEffect(() => {
        const data = async () => {
            try {
                setLoading(true);
                const id: number = selectedRecord.id;

                const [datePattern, accrualDates] = await Promise.allSettled([
                    timekeeperService.date_pattern.getOne(id),
                    timekeeperService.accrual_date.getAll()
                ]);

                if (datePattern.status === 'fulfilled') {
                    const data = datePattern.value;
                    setDatePattern(data);
                }

                if (accrualDates.status === 'fulfilled') {
                    const data = accrualDates ? accrualDates.value : [];
                    setAccrualDate(data);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        if (open && selectedRecord) {
            data()
        }
    }, [open, selectedRecord]);

    const InfoRow = ({ label, value }: { label: string; value: string | number | React.ReactNode }) => (
        <Grid container spacing={2} sx={{ mb: 2, border: '1px solid #ccc', borderRadius: '4px', padding: '8px' }} direction="row" >
            <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
                <Typography variant="body1">
                    {label}:
                </Typography>

            </Grid>
            <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
                <Typography variant="body1">
                    {value}
                </Typography>
            </Grid>
        </Grid>
    );

    const closeForm = () => {
        setOpen(false);
        cleanSelected();
        setDatePattern(null);
        setAccrualDate([]);
        setLoading(false);
    }

    const dateReference = useMemo(() => {
        if (accrual_date && date_pattern) {
            return accrual_date.find((date: any) => date.accrual_date_id === date_pattern.reference_date_id);
        }
        return null;
    }, [accrual_date, date_pattern]);

    return (
        <Dialog
            open={open}
            onClose={closeForm}
            disableRestoreFocus
            fullWidth
            maxWidth="sm"
            PaperComponent={PaperComponent}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
                {t('accruals.date_patterns.dialog.titles.info')}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={closeForm}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                {date_pattern && (
                    <Box>
                        {/* Campo nombre */}
                        <InfoRow
                            label={t('accruals.date_patterns.dialog.inputs.name')}
                            value={date_pattern.name ? date_pattern.name : ''}
                        />
                        {/* Campo Fecha de Referencia */}
                        <InfoRow
                            label={t('accruals.date_patterns.dialog.inputs.date_reference')}
                            value={dateReference ? dateReference.name : ''}
                        />
                        {/* Campo Frecuencia */}
                        <InfoRow
                            label={t('accruals.date_patterns.dialog.inputs.frequency')}
                            value={`${frequencyOptions.find(option => option.value === date_pattern.frequency_unit_time as FrequencyUnitTime)?.label}`}
                        />
                        {/* Campo cantidad de frecuencia */}
                        <InfoRow
                            label={t("accruals.date_patterns.dialog.inputs.frequency_amount")}
                            value={`${date_pattern ? date_pattern.frequency_amount : ''} `}
                        />
                        {/* Campo inicio del patrón */}
                        <InfoRow
                            label={t('accruals.date_patterns.dialog.inputs.start_of_pattern')}
                            value={patternStartDateOptions.find(option => option.value === date_pattern.pattern_start_date)?.label}
                        />
                        {
                            date_pattern.frequency_unit_time === FrequencyUnitTime.DIAS && (
                                <InfoRow
                                    label={t('accruals.date_patterns.dialog.inputs.day_of_the_month_to_start')}
                                    value={date_pattern ? date_pattern.frequency_unit_time : ''}
                                />
                            )
                        }

                        {
                            date_pattern.frequency_unit_time === FrequencyUnitTime.SEMANAS && (
                                <InfoRow
                                    label={t('accruals.date_patterns.dialog.inputs.day_of_week_to_start')}
                                    value={daysOfWeekOptions.find(option => option.value === date_pattern.day_week_to_start)?.label}
                                />
                            )
                        }
                        {
                            date_pattern.frequency_unit_time === FrequencyUnitTime.MESES && (
                                <InfoRow
                                    label={t('accruals.date_patterns.dialog.inputs.day_of_the_month_to_start')}
                                    value={date_pattern ? date_pattern.day_month_to_start : ''}
                                />
                            )
                        }
                        {
                            date_pattern.frequency_unit_time === FrequencyUnitTime.ANOS && (
                                <>
                                    <InfoRow
                                        label={t('accruals.date_patterns.dialog.inputs.month_to_start')}
                                        value={MonthOfYear.find(option => option.value === date_pattern.month_to_start)?.label}
                                    />
                                    <InfoRow
                                        label={t('accruals.date_patterns.dialog.inputs.day_of_the_month_to_start')}
                                        value={date_pattern ? date_pattern.month_to_start : ''}
                                    />
                                </>

                            )
                        }
                        {
                            date_pattern.frequency_unit_time === FrequencyUnitTime.PERIODO_PAGO && (
                                <InfoRow
                                    label={t('accruals.date_patterns.dialog.inputs.payment_period_start_date')}
                                    value={date_pattern ? date_pattern.day_period_to_start : ''}
                                />
                            )
                        }
                        {/* Campo tipo de compensación */}
                        <InfoRow
                            label={t('accruals.date_patterns.dialog.inputs.chronological_difference')}
                            value={compensationTypeOptions.find(option => option.value === date_pattern.compensation_type)?.label}
                        />
                        {/* Equivalencia de tiempo completo */}
                        <InfoRow
                            label={t('accruals.date_patterns.dialog.inputs.full_time_equivalent')}
                            value={formatDurationToHHMM(date_pattern.full_time_equivalent)}
                        />
                    </Box>
                )}
                {
                    loading && (
                        <Box display="flex" justifyContent="center" alignItems="center">
                            <CircularProgress color='inherit' size={30}/>
                        </Box>
                    )
                }
            </DialogContent>
            <DialogActions>
                <Button variant="contained" color="error" onClick={closeForm}>
                    {t('general.buttons.close')}
                </Button>
            </DialogActions>
        </Dialog>
    );

}

export { InfoDatePattern };